'use client';

import { useRef, useState, type ReactNode } from 'react';
import { Link } from '@/i18n/navigation';
import { cn } from '@/helpers/utils';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import SvgChevronDown from './svg/SvgChevronDown';
import SvgChevronRight from './svg/SvgChevronRight';

type SubmenuItem = {
    href: string;
    label: string;
};

type NavSubmenuProps = {
    items: SubmenuItem[];
    children: ReactNode;
};

export default function NavSubmenu({ items, children }: NavSubmenuProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    useOnClickOutside(ref, () => setOpen(false));

    return (
        <div ref={ref} className="group relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="true"
                className="inline-flex items-center gap-2 leading-none mouse-hover:bg-purple-800 mouse-hover:text-white pt-10 pb-6 px-6 mb-4 rounded-bl-[15px] rounded-br-[15px] font-medium transition-colors duration-200"
            >
                {children}
                <span className="relative top-0.5">
                    <SvgChevronDown
                        width={10}
                        height={6}
                        className={cn(
                            'transform transition-transform duration-200',
                            open ? 'rotate-180' : 'group-hover:rotate-180'
                        )}
                    />
                </span>
            </button>

            {/* Anchor to the button's border-box bottom: subtract the button's
                mb-4 so only the pt-6 hover bridge sits below it (the margin is
                otherwise included in the wrapper height that top-full resolves against). */}
            <div className={cn('absolute top-[calc(100%-1rem)] left-0 pt-6', open ? 'block' : 'hidden group-hover:block')}>
                <div className="w-max bg-white flex flex-col rounded-[15px] px-7 shadow-submenu">
                    {items.map(({ href, label }) => (
                        <Link
                            href={href}
                            key={href}
                            onClick={() => setOpen(false)}
                            className="text-black mouse-hover:text-purple-800 flex justify-between items-center gap-2 py-6 border-b last:border-0 transition-colors duration-200 submenu-item"
                        >
                            {label}
                            <span className="relative top-0.5 shrink-0 w-1.5 text-purple-800 transition-opacity duration-200">
                                <SvgChevronRight />
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
